import React, { useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useI18n } from '@/features/i18n/useI18n';
import styles from './FileUpload.module.scss';

export interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  onFileRemove?: (index: number) => void;
  acceptedTypes?: string[];
  maxFileSize?: number; // en bytes
  maxFiles?: number;
  multiple?: boolean;
  disabled?: boolean;
  existingFiles?: File[];
  showPreview?: boolean;
  dragDropText?: string;
  browseText?: string;
  className?: string;
}

const DEFAULT_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'application/zip',
  'application/x-zip-compressed'
];

const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesSelected,
  onFileRemove,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxFiles = 10,
  multiple = true,
  disabled = false,
  existingFiles = [],
  showPreview = true,
  dragDropText,
  browseText,
  className = ''
}) => {
  const { t } = useI18n();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Validar tipo de archivo
    if (!acceptedTypes.includes(file.type)) {
      return t('file_upload.error_invalid_type');
    }

    // Validar tamaño
    if (file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      return t('file_upload.error_file_too_large', { maxSize: maxSizeMB });
    }

    // Validar nombre de archivo
    if (file.name.length > 255) {
      return t('file_upload.error_filename_too_long');
    }

    return null;
  }, [acceptedTypes, maxFileSize, t]);

  const processFiles = useCallback((files: FileList | File[]) => {
    if (disabled || isProcessing) return;

    setIsProcessing(true);
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const errors: string[] = [];

    // Validar límite de archivos
    if (existingFiles.length + fileArray.length > maxFiles) {
      errors.push(t('file_upload.error_too_many_files', { maxFiles }));
    }

    // Validar cada archivo
    fileArray.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    // Mostrar errores si los hay
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    // Procesar archivos válidos
    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }

    setIsProcessing(false);
  }, [disabled, isProcessing, existingFiles.length, maxFiles, validateFile, onFilesSelected, t]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, [disabled, processFiles]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    e.target.value = '';
  }, [processFiles]);

  const handleBrowseClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleFileRemove = useCallback((index: number) => {
    if (onFileRemove) {
      onFileRemove(index);
    }
  }, [onFileRemove]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File): string => {
    if (file.type.startsWith('image/')) return '🖼️';
    if (file.type.includes('pdf')) return '📄';
    if (file.type.includes('word')) return '📝';
    if (file.type.includes('excel') || file.type.includes('spreadsheet')) return '📊';
    if (file.type.includes('zip')) return '📦';
    return '📎';
  };

  return (
    <div className={`${styles.fileUpload} ${className}`}>
      {/* Zona de drag & drop */}
      <div
        className={`${styles.dropZone} ${isDragOver ? styles.dragOver : ''} ${disabled ? styles.disabled : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptedTypes.join(',')}
          onChange={handleFileInputChange}
          disabled={disabled}
          className={styles.hiddenInput}
        />
        
        <div className={styles.dropZoneContent}>
          <div className={styles.uploadIcon}>📁</div>
          <p className={styles.dragDropText}>
            {dragDropText || t('file_upload.drag_drop_text')}
          </p>
          <p className={styles.browseText}>
            {browseText || t('file_upload.browse_text')}
          </p>
          <p className={styles.supportedFormats}>
            {t('file_upload.supported_formats')}
          </p>
        </div>
      </div>

      {/* Indicador de procesamiento */}
      {isProcessing && (
        <div className={styles.processingIndicator}>
          <div className={styles.spinner}></div>
          <span>{t('file_upload.processing')}</span>
        </div>
      )}

      {/* Preview de archivos */}
      {showPreview && existingFiles.length > 0 && (
        <div className={styles.filePreview}>
          <h4 className={styles.previewTitle}>{t('file_upload.attached_files')}</h4>
          <div className={styles.fileList}>
            {existingFiles.map((file, index) => (
              <div key={`${file.name}-${index}`} className={styles.fileItem}>
                <div className={styles.fileInfo}>
                  <span className={styles.fileIcon}>{getFileIcon(file)}</span>
                  <div className={styles.fileDetails}>
                    <span className={styles.fileName} title={file.name}>
                      {file.name}
                    </span>
                    <span className={styles.fileSize}>
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                </div>
                {onFileRemove && (
                  <button
                    type="button"
                    onClick={() => handleFileRemove(index)}
                    className={styles.removeButton}
                    title={t('file_upload.remove_file')}
                  >
                    ❌
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 